import { useMemo, useState } from 'react';

import { categoryApi } from '@/api/services/categoryApi';
import { Button, EmptyState } from '@/components/ui';
import type { ChinaAppSysGroupCategory } from '@/types/entities';

import { AdminCategoryForm } from './forms/AdminCategoryForm';
import { useAdminList } from './lists/adminList';
import { MasterDetail, type MasterItem } from './MasterDetail';

/** Port von `IwmAdminKategorie.razor` in der Gestalt aus REQ-25 §4.3 (REQ-28 §4). */
const load = () => categoryApi.get();

/** Reihenfolge wie in der Navigation: `MenuSortierung`, bei Gleichstand die Bezeichnung. */
function byMenuOrder(a: ChinaAppSysGroupCategory, b: ChinaAppSysGroupCategory): number {
  if (a.MenuSortierung !== b.MenuSortierung) return a.MenuSortierung - b.MenuSortierung;
  return (a.Bezeichnung ?? '').localeCompare(b.Bezeichnung ?? '', 'de-DE');
}

function toItem(category: ChinaAppSysGroupCategory): MasterItem {
  return {
    id: category.Id,
    label: category.Bezeichnung ?? '',
    // Die Zahl steht für sich; der Kurzhinweis nennt die Beschriftung des Feldes.
    meta: <span title="Menu Order">{category.MenuSortierung}</span>,
  };
}

export function AdminCategories() {
  const { rows, loading, error, reload } = useAdminList(load);
  // `null` heißt „nichts gewählt", `0` steht für eine neue Kategorie.
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const items = useMemo(() => [...rows].sort(byMenuOrder).map(toItem), [rows]);

  const saved = async (id: number) => {
    setSelectedId(id > 0 ? id : null);
    await reload();
  };

  return (
    <MasterDetail
      listLabel="Kategorien"
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      loading={loading}
      error={error}
      onRetry={() => {
        void reload();
      }}
      searchPlaceholder="Name"
      emptyText="Keine Kategorien"
      toolbar={
        <Button
          content="New"
          iconClass="fa-solid fa-plus"
          onClick={() => {
            setSelectedId(0);
          }}
        />
      }
    >
      {selectedId === null ? (
        <EmptyState
          iconClass="fa-solid fa-folder"
          title="Keine Kategorie gewählt"
          description="Wählen Sie links eine Kategorie aus oder legen Sie eine neue an."
        />
      ) : (
        <AdminCategoryForm id={selectedId} onSave={saved} />
      )}
    </MasterDetail>
  );
}

export default AdminCategories;
